"use client";

import { useEffect } from "react";
import { GA4_ID, GOOGLE_ADS_ID } from "@/lib/config";
import { CONSENT_EVENT, PRIVATE_AREA, getAdsConsent, getStatsConsent } from "@/lib/consent";
import { adsConversion } from "@/lib/adsConversions";
import { missingClickParams } from "@/lib/adClick";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * The Google tag (gtag.js), loaded only after the visitor accepted at least
 * one purpose in the cookie banner: Google Ads conversion measurement and,
 * once a GA4 property is set up, Google Analytics statistics (11.10.2026).
 * Consent Mode v2 starts "denied" and is updated to exactly what was
 * accepted in the same step, so Google never sees a visit without consent.
 * A changed choice reloads the page (reopenConsent), so loading once is enough.
 */
export function GoogleAdsTag() {
  useEffect(() => {
    const load = () => {
      if (PRIVATE_AREA.test(window.location.pathname)) return;
      const ads = getAdsConsent() === "granted";
      const stats = Boolean(GA4_ID) && getStatsConsent() === "granted";
      if ((!ads && !stats) || document.getElementById("gtag-ads")) return;
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        // gtag.js expects the arguments object itself, not an array.
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer!.push(arguments);
      };
      window.gtag("consent", "default", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
      // Only what was accepted; never ad personalisation (remarketing).
      window.gtag("consent", "update", {
        ad_storage: ads ? "granted" : "denied",
        ad_user_data: ads ? "granted" : "denied",
        ad_personalization: "denied",
        analytics_storage: stats ? "granted" : "denied",
      });
      // A visitor from an ad who clicked on before accepting: put the ad click
      // ids back into the address while gtag.js reads it, then take them out
      // again (tested 10.10.2026: a page_location in the config is ignored
      // for this, the address itself works).
      const added = ads ? missingClickParams() : [];
      let tempUrl: string | null = null;
      if (added.length) {
        const u = new URL(window.location.href);
        for (const [k, v] of added) u.searchParams.set(k, v);
        tempUrl = u.href;
        window.history.replaceState(null, "", tempUrl);
      }
      window.gtag("js", new Date());
      if (ads) window.gtag("config", GOOGLE_ADS_ID, { allow_ad_personalization_signals: false });
      // Statistics only: no Google signals (cross-device), no ad personalisation.
      if (stats) window.gtag("config", GA4_ID, { allow_google_signals: false, allow_ad_personalization_signals: false });
      const s = document.createElement("script");
      s.id = "gtag-ads";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${ads ? GOOGLE_ADS_ID : GA4_ID}`;
      document.head.appendChild(s);
      if (tempUrl) {
        const started = Date.now();
        const restore = () => {
          // gtag.js keeps the id in its _gcl_* cookie; wait for it (at most 6 s).
          if (!/(?:^|;\s*)_gcl_(?:aw|gb|dc)=/.test(document.cookie) && Date.now() - started < 6000) {
            window.setTimeout(restore, 300);
            return;
          }
          if (window.location.href !== tempUrl) return; // the visitor navigated on
          const u = new URL(tempUrl);
          for (const [k] of added) u.searchParams.delete(k);
          window.history.replaceState(null, "", u.href);
        };
        window.setTimeout(restore, 300);
      }
    };
    load();
    window.addEventListener(CONSENT_EVENT, load);
    // A tap on WhatsApp or Telegram is a contact (sent only with Ads consent).
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a) return;
      const method = /wa\.me|whatsapp/i.test(a.href) ? "whatsapp" : /t\.me|telegram/i.test(a.href) ? "telegram" : null;
      if (method) adsConversion("contact", { method });
    };
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener(CONSENT_EVENT, load);
      document.removeEventListener("click", onClick, true);
    };
  }, []);
  return null;
}
